import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ChevronLeft, ChevronRight, Save, Trash2, Eraser, Copy } from "lucide-react";
import { toast } from "sonner";

const API = "/api";

// ── Time grid: 6:00 AM → 11:30 PM in 30-min slots (mirrors My Schedule) ──
const TIME_SLOTS = [];
for (let hour = 6; hour < 24; hour++) {
  for (let min = 0; min < 60; min += 30) {
    const h = hour.toString().padStart(2, "0");
    const m = min.toString().padStart(2, "0");
    TIME_SLOTS.push(`${h}:${m}`);
  }
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Backend stores weekday as 0=Monday..6=Sunday (Python convention).
// JS Date.getUTCDay() gives 0=Sunday..6=Saturday → convert with this helper.
function jsDowToPyWeekday(jsDow) {
  // js: 0=Sun,1=Mon,...6=Sat  →  py: 0=Mon,1=Tue,...,6=Sun
  return jsDow === 0 ? 6 : jsDow - 1;
}

function formatTime12h(time24) {
  const [hours, minutes] = time24.split(":");
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${minutes} ${ampm}`;
}

function timeStrToMin(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}
function minToTimeStr(min) {
  const h = Math.floor(min / 60).toString().padStart(2, "0");
  const m = (min % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

// Build the Sun→Sat week containing the given date.
function buildWeek(anchorDate) {
  const date = new Date(anchorDate);
  const dow = date.getUTCDay(); // 0=Sun..6=Sat
  const sunday = new Date(date);
  sunday.setUTCDate(date.getUTCDate() - dow);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(sunday);
    d.setUTCDate(sunday.getUTCDate() + i);
    days.push({
      jsDow: d.getUTCDay(),
      pyDow: jsDowToPyWeekday(d.getUTCDay()),
      dayName: DAY_NAMES[d.getUTCDay()],
      dayNum: d.getUTCDate(),
      month: MONTH_NAMES[d.getUTCMonth()],
      year: d.getUTCFullYear(),
      isoDate: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`,
    });
  }
  return days;
}

// Expand backend windows (days: [{weekday, start, end}]) into a Set of "pyDow|HH:MM" 30-min slot keys
function windowsToSlotSet(days) {
  const set = new Set();
  for (const d of days || []) {
    const startMin = timeStrToMin(d.start);
    const endMin = timeStrToMin(d.end);
    for (let m = startMin; m + 30 <= endMin; m += 30) {
      set.add(`${d.weekday}|${minToTimeStr(m)}`);
    }
  }
  return set;
}

// Collapse the slot set back into contiguous windows for backend.
function slotSetToWindows(slotSet) {
  // Group by weekday
  const byDay = {};
  for (const key of slotSet) {
    const [dowStr, time] = key.split("|");
    const dow = parseInt(dowStr, 10);
    (byDay[dow] = byDay[dow] || []).push(timeStrToMin(time));
  }
  const windows = [];
  for (const dowStr of Object.keys(byDay)) {
    const dow = parseInt(dowStr, 10);
    const sorted = byDay[dow].sort((a, b) => a - b);
    let runStart = sorted[0];
    let prev = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === prev + 30) {
        prev = sorted[i];
      } else {
        windows.push({ weekday: dow, start: minToTimeStr(runStart), end: minToTimeStr(prev + 30) });
        runStart = sorted[i];
        prev = sorted[i];
      }
    }
    windows.push({ weekday: dow, start: minToTimeStr(runStart), end: minToTimeStr(prev + 30) });
  }
  return windows;
}

export default function MyAvailability() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slotSet, setSlotSet] = useState(() => new Set());
  const [originalKey, setOriginalKey] = useState("");
  const [anchorDate, setAnchorDate] = useState(() => {
    const t = new Date();
    return new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate()));
  });

  const weekDays = useMemo(() => buildWeek(anchorDate), [anchorDate]);
  const weekStartLabel = weekDays[0]
    ? `Week of ${weekDays[0].month} ${weekDays[0].dayNum}, ${weekDays[0].year}`
    : "";

  // Drag-to-paint state
  const [paintMode, setPaintMode] = useState(null); // "add" | "remove" | null
  const [isPainting, setIsPainting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/tutors/my-availability`, { headers: getAuthHeader() });
      const set = windowsToSlotSet(r.data.days || []);
      setSlotSet(set);
      setOriginalKey(JSON.stringify([...set].sort()));
    } catch {
      setSlotSet(new Set());
      setOriginalKey("[]");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isDirty = useMemo(
    () => JSON.stringify([...slotSet].sort()) !== originalKey,
    [slotSet, originalKey]
  );

  const toggleSlot = (pyDow, time) => {
    const key = `${pyDow}|${time}`;
    const next = new Set(slotSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSlotSet(next);
  };

  const paintSlot = (pyDow, time, mode) => {
    const key = `${pyDow}|${time}`;
    setSlotSet((prev) => {
      const next = new Set(prev);
      if (mode === "add") next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const handleMouseDown = (pyDow, time) => {
    const key = `${pyDow}|${time}`;
    const mode = slotSet.has(key) ? "remove" : "add";
    setPaintMode(mode);
    setIsPainting(true);
    paintSlot(pyDow, time, mode);
  };
  const handleMouseEnter = (pyDow, time) => {
    if (!isPainting || !paintMode) return;
    paintSlot(pyDow, time, paintMode);
  };
  const handleMouseUp = () => {
    setIsPainting(false);
    setPaintMode(null);
  };

  useEffect(() => {
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, []);

  const clearWeekday = (pyDow) => {
    setSlotSet((prev) => {
      const next = new Set(prev);
      for (const k of prev) if (k.startsWith(`${pyDow}|`)) next.delete(k);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const days = slotSetToWindows(slotSet);
      await axios.put(`${API}/tutors/my-availability`, { days }, { headers: getAuthHeader() });
      setOriginalKey(JSON.stringify([...slotSet].sort()));
      toast.success(
        days.length === 0
          ? "Availability cleared"
          : `Saved ${days.length} window${days.length === 1 ? "" : "s"} across the week`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    fetchData();
    toast.info("Unsaved changes discarded");
  };

  const goPrev = () => {
    const d = new Date(anchorDate);
    d.setUTCDate(d.getUTCDate() - 7);
    setAnchorDate(d);
  };
  const goNext = () => {
    const d = new Date(anchorDate);
    d.setUTCDate(d.getUTCDate() + 7);
    setAnchorDate(d);
  };
  const goToday = () => {
    const t = new Date();
    setAnchorDate(new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate())));
  };

  // Copy a given weekday's pattern to other weekdays (default: Mon → Tue-Fri).
  const copyWeekdayTo = (srcPyDow, targetPyDows) => {
    const srcTimes = [];
    for (const k of slotSet) {
      const [dStr, time] = k.split("|");
      if (parseInt(dStr, 10) === srcPyDow) srcTimes.push(time);
    }
    setSlotSet((prev) => {
      const next = new Set(prev);
      // Clear targets first
      for (const k of prev) {
        const [dStr] = k.split("|");
        if (targetPyDows.includes(parseInt(dStr, 10))) next.delete(k);
      }
      // Apply source pattern to each target
      for (const tDow of targetPyDows) {
        for (const time of srcTimes) {
          next.add(`${tDow}|${time}`);
        }
      }
      return next;
    });
    toast.success(
      srcTimes.length === 0
        ? `Cleared selected weekdays (source had no active slots)`
        : `Copied ${srcTimes.length} slot${srcTimes.length === 1 ? "" : "s"} to ${targetPyDows.length} weekday${targetPyDows.length === 1 ? "" : "s"}`
    );
  };

  const handleCopyMondayToWeekdays = () => {
    // Monday = pyDow 0, Tue-Fri = [1,2,3,4]
    copyWeekdayTo(0, [1, 2, 3, 4]);
  };

  const totalActiveSlots = slotSet.size;

  return (
    <div className="space-y-6 select-none" data-testid="my-availability-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="my-availability-title">
            My Availability
          </h1>
          <p className="text-sm text-slate-500 mt-1">{weekStartLabel}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium"
            data-testid="active-slots-count"
          >
            {totalActiveSlots} slot{totalActiveSlots === 1 ? "" : "s"} active
          </span>
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium">
            Europe/Istanbul
          </span>
        </div>
      </div>

      {/* Navigation row + actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goPrev} data-testid="prev-week-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="today-btn">
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={goNext} data-testid="next-week-btn">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDiscard}
              data-testid="discard-btn"
              className="text-slate-600"
            >
              Discard
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-availability-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isDirty ? "Save Changes" : "Saved"}
          </Button>
        </div>
      </div>

      {/* Helper hint */}
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 flex items-center justify-between flex-wrap gap-2">
        <span>
          💡 Click a slot to toggle availability. Drag to paint multiple slots at once. Weekly
          pattern repeats every week — date headers are just for orientation.
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyMondayToWeekdays}
          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
          data-testid="copy-mon-to-weekdays-btn"
        >
          <Copy className="w-3 h-3 mr-1" /> Copy Mon → Tue-Fri
        </Button>
      </div>

      {/* Schedule Grid */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[calc(100vh-340px)]">
              <table className="w-full min-w-[800px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="py-3 px-4 text-left text-sm font-semibold text-slate-600 w-24 sticky left-0 bg-slate-50 z-20">
                      Time
                    </th>
                    {weekDays.map((day) => {
                      const dayHasSlots = [...slotSet].some((k) => k.startsWith(`${day.pyDow}|`));
                      return (
                        <th
                          key={day.isoDate}
                          className="py-3 px-2 text-center text-sm font-semibold text-slate-600 min-w-[120px] bg-slate-50"
                        >
                          <div className="flex items-center justify-center gap-1">
                            <span>{day.dayName}</span>
                            {dayHasSlots && (
                              <button
                                type="button"
                                onClick={() => clearWeekday(day.pyDow)}
                                className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition"
                                title={`Clear all ${day.dayName} slots`}
                                data-testid={`clear-day-${day.pyDow}`}
                              >
                                <Eraser className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <div className="text-xs font-normal text-slate-400">
                            {day.month} {day.dayNum}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {TIME_SLOTS.map((time) => (
                    <tr key={time} className="border-b border-slate-100">
                      <td className="py-1 px-4 text-sm text-slate-600 font-medium whitespace-nowrap sticky left-0 bg-white z-10">
                        {formatTime12h(time)}
                      </td>
                      {weekDays.map((day) => {
                        const key = `${day.pyDow}|${time}`;
                        const isActive = slotSet.has(key);
                        return (
                          <td key={`${day.isoDate}-${time}`} className="py-1 px-1">
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleMouseDown(day.pyDow, time);
                              }}
                              onMouseEnter={() => handleMouseEnter(day.pyDow, time)}
                              className={[
                                "w-full min-h-[36px] rounded-md transition-all flex items-center justify-center text-xs font-medium border",
                                isActive
                                  ? "bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600 shadow-sm"
                                  : "bg-white border-slate-200 border-dashed text-slate-300 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-600",
                              ].join(" ")}
                              data-testid={`avail-cell-${day.pyDow}-${time}`}
                              aria-pressed={isActive}
                            >
                              {isActive ? "Available" : "+"}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk actions */}
      {!loading && (
        <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-slate-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-emerald-500 inline-block"></span>
              Available
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded border border-dashed border-slate-300 inline-block"></span>
              Not available
            </span>
          </div>
          {slotSet.size > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSlotSet(new Set())}
              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              data-testid="clear-all-btn"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Clear All
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
